import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { Typography, Stack } from '@mui/material';
import { Activity, icons } from '../../../shared/types/Activity';
import { format, formatDistance } from 'date-fns';
import { styled } from '@mui/material/styles';

export const IconCell = ({
  icon,
  iconHover,
}: {
  icon: string;
  iconHover: string;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <i
      className={hover ? iconHover : icon}
      style={{
        fontSize: typographyTokens.fontSizes.body,
        marginTop: '7px',
        color: hover ? '#2372B9' : '#7E8083',
      }}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    ></i>
  );
};

const VerticalDivider = () => {
  return (
    <Typography
      component="span"
      sx={{
        width: '2px',
        minHeight: '16px',
        border: 'solid 0px #7E8083',
        backgroundColor: '#7E8083',
        marginLeft: '21px',
        flex: 1,
        alignSelf: 'stretch',
      }}
    ></Typography>
  );
};

interface TimerDotProps {
  isFirst?: boolean;
  isLast?: boolean;
  data: Activity;
}

const TimerDot = ({ isFirst, isLast, data }: TimerDotProps) => {
  return (
    <Stack
      display="flex"
      flexDirection="column"
      justifyContent="center"
      sx={{
        alignItems: 'left',
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <Stack
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'center', md: 'space-between' }}
        alignItems="top"
      >
        <Stack
          display="flex"
          flexDirection="row"
          alignItems="top"
          justifyContent="center"
        >
          <Box
            alignItems="center"
            sx={{
              position: 'relative',
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              component="div"
              sx={{
                width: '32px',
                height: '32px',
                border: 'solid 2px #7E8083',
                borderRadius: '17px',
                textAlign: 'center',
                margin: '5px',
              }}
            >
              <IconCell
                icon={icons[data.action + data.trackableType]}
                iconHover="fa-solid fa-circle-check hovered-icon"
              />
            </Typography>
            {isLast ? <></> : <VerticalDivider />}
          </Box>

          <Typography
            component="div"
            sx={{
              fontSize: typographyTokens.fontSizes.base,
              paddingLeft: '30px',
              color: '#646669',
              maxWidth: '900px',
              height: '100%',
              textAlign: 'left',
              paddingTop: '8px',
              wordWrap: 'break-word',
            }}
          >
            {data.title?.replace(/pending/gi, 'Locked')}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: typographyTokens.fontSizes.body,
            minWidth: '180px',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            paddingTop: '8px',
            pb: { xs: 2, md: 0 },
          }}
        >
          {format(new Date(data.createdAt), 'MM/dd/yyyy | hh:mm a')}
        </Typography>
      </Stack>
    </Stack>
  );
};

const TimeLine = ({ activities }: { activities: Activity[] }) => {
  const groupedActivities = React.useMemo(() => {
    const groups: [string, Activity[]][] = [];
    activities.forEach((a) => {
      const header = formatDistance(new Date(a.createdAt), new Date(), {
        addSuffix: true,
      });
      const foundGroup = groups.find((g) => g[0] === header);
      if (!foundGroup) {
        groups.push([header, []]);
      }
      groups[groups.length - 1][1].push(a);
    });
    return groups;
  }, [activities]);
  const StyledHeader = styled(Typography)(({ theme }) => ({
    fontSize: typographyTokens.fontSizes.body,
    color: theme.palette.gray.darkGray1,
    fontWeight: typographyTokens.fontWeights.semibold,
    textTransform: 'capitalize',
    borderBottom: 'solid 1px #E5E9EA',
    marginLeft: 90,
    paddingBottom: 5,
    marginTop: 20,
    width: 'calc(100% - 100px)',
  }));

  return (
    <Box aria-label="ActivityData">
      {groupedActivities.map((group, idx) => (
        <div key={idx}>
          <StyledHeader>{group[0]}</StyledHeader>
          {group[1].map((activity, i) => (
            <TimerDot
              key={i}
              data={activity}
              isLast={i === group[1].length - 1}
            />
          ))}
        </div>
      ))}
    </Box>
  );
};

export default TimeLine;
